import type { Ref } from 'react';
import { Box, Paper, Skeleton, Stack } from '@mui/material';

export interface LoadingSkeletonProps {
  /**
   * Which loading shape to render:
   * - `table` — header + N rows
   * - `card` — a row of KPI tiles
   * - `detail` — title + key/value lines
   */
  variant?: 'table' | 'card' | 'detail';
  /** Row count for `table`/`detail` variants. */
  rows?: number;
  ref?: Ref<HTMLDivElement>;
}

/**
 * Shimmer placeholder for the three shapes the portal loads most. Uses MUI
 * `Skeleton` throughout so it tracks the theme automatically.
 */
export function LoadingSkeleton({ variant = 'table', rows = 5, ref }: LoadingSkeletonProps) {
  if (variant === 'card') {
    return (
      <Box
        ref={ref}
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 2,
        }}
      >
        {Array.from({ length: 4 }).map((_, i) => (
          <Paper key={i} variant="outlined" sx={{ p: '18px 20px', borderRadius: '4px' }}>
            <Stack spacing={1.5}>
              <Skeleton animation="wave" variant="text" width="55%" height={12} />
              <Skeleton animation="wave" variant="text" width="70%" height={30} />
              <Skeleton animation="wave" variant="text" width="40%" height={12} />
            </Stack>
          </Paper>
        ))}
      </Box>
    );
  }

  if (variant === 'detail') {
    return (
      <Paper ref={ref} variant="outlined" sx={{ p: '18px 20px', borderRadius: '4px' }}>
        <Stack spacing={2}>
          <Skeleton animation="wave" variant="text" width="45%" height={22} />
          {Array.from({ length: rows }).map((_, i) => (
            <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', gap: 3 }}>
              <Skeleton animation="wave" variant="text" width="28%" height={12} />
              <Skeleton animation="wave" variant="text" width="38%" height={12} />
            </Box>
          ))}
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper ref={ref} variant="outlined" sx={{ p: '14px 18px', borderRadius: '4px' }}>
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          pb: 1.75,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        {['28%', '18%', '20%', '14%'].map((w, i) => (
          <Skeleton key={i} variant="text" width={w} height={12} />
        ))}
      </Box>
      {Array.from({ length: rows }).map((_, i) => (
        <Box
          key={i}
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            py: 1.75,
            borderBottom: i < rows - 1 ? '1px solid' : 'none',
            borderColor: 'divider',
          }}
        >
          <Skeleton animation="wave" variant="text" width="28%" height={14} />
          <Skeleton animation="wave" variant="text" width="18%" height={14} />
          <Skeleton animation="wave" variant="text" width="20%" height={14} />
          <Skeleton animation="wave" variant="text" width="14%" height={14} />
        </Box>
      ))}
    </Paper>
  );
}
